import helperUtils from 'utils/Helpers';

const { getProp } = helperUtils;

/**
 * Builds the accessible alt text for a SKU image.
 *
 * `sku` is normalized to an object before destructuring and every string field is guarded before
 * `.length`/interpolation: destructuring defaults only replace `undefined`, not `null`, and OIS
 * returns fields like `size`/`variationValue` as explicit `null` (OXS omits them). This runs in
 * ReplenProductItem's render, so an unhandled throw would blank the carousel tile (ECSC-9774).
 *
 * @param {object} [sku] - SKU object; may be null/undefined or carry null string fields
 * @param {string} [type] - Optional sku type ('sample' | 'reward'); skips imageAltText when type is 'sample' or 'reward'
 * @returns {string} Space-normalized alt text
 */
const getImageAltText = (sku, type) => {
    const {
        brandName = '',
        heroImageAltText = '',
        badgeAltText = '',
        primaryProduct = {},
        imageAltText = '',
        productName = '',
        size = '',
        variationValue = '',
        variationType = ''
    } = sku || {};

    const isRewardOrSample = type === 'sample' || type === 'reward';
    let altText = '';

    if ((imageAltText || '').length > 0 && !isRewardOrSample) {
        altText = `${imageAltText}`;
    } else {
        altText = `${brandName || ''} ${productName || ''}`;
    }

    if (variationType === 'Color' && variationValue) {
        altText = `${altText} ${variationValue}`;
    }

    if ((size || '').length > 0) {
        altText = `${altText} ${size}`;
    }

    if (heroImageAltText || primaryProduct?.heroImageAltText) {
        altText = `${altText} ${heroImageAltText ? heroImageAltText : primaryProduct?.heroImageAltText}`;
    }

    return `${altText} ${badgeAltText || ''}`.replace(/\s+/g, ' ').trim();
};

/**
 * Merges product-level fallbacks (name, brand, variation, size, alt text) into a SKU, then
 * returns its accessible image alt text via getImageAltText.
 *
 * @param {object} [currentSku] - SKU object to supplement
 * @param {object} [product] - Product providing fallback values from productDetails/currentSku
 * @param {string} [skutype] - Optional sku type forwarded to getImageAltText
 * @returns {string} Space-normalized alt text
 */
const supplementAltTextWithProduct = (currentSku = {}, product = {}, skutype) => {
    const sku = { ...currentSku };

    sku.imageAltText = sku.imageAltText || product?.productDetails?.imageAltText;
    sku.heroImageAltText = sku.heroImageAltText || product?.productDetails?.heroImageAltText;

    sku.productName = sku.productName || product?.productDetails?.displayName;
    sku.brandName = sku.brandName || getProp(product.productDetails, 'brand.displayName');

    sku.variationType = sku.variationType || getProp(product, 'currentSku.variationType');
    sku.variationValue = sku.variationValue || getProp(product, 'currentSku.variationValue');
    sku.size = sku.size || getProp(product, 'currentSku.size');

    return getImageAltText(sku, skutype);
};

export {
    getImageAltText, supplementAltTextWithProduct
};
